import {Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild} from '@angular/core';
import {DxDataGridComponent} from 'devextreme-angular';
import {GridSetting, GridSettings, GridState, User} from '../../../shared';
import {take, takeUntil} from 'rxjs/operators';
import {UserApi} from '../../../api';
import CustomStore from 'devextreme/data/custom_store';
import {FilterOperationModel, LoadOptions, SearchNestedList} from '../../../shared/models/shared.model';
import {FilterBuilderService} from '../../../service/filter-builder.service';
import {ProductMappingService} from '../../../api/product-mapping.api';
import {Subject} from 'rxjs';
import {ProductEditComponent} from '../product-mapping/product-edit/product-edit.component';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';

@Component ({
	selector: 'app-product-master-data-grid',
	templateUrl: 'product-master-data-grid.component.html'
})

export class ProductMasterDataGridComponent implements OnInit, OnChanges {
	@Input() pageName: string;
	@Input() masterGridName: string;
	@Input() isCreateProduct: boolean;

	@Output() productMappingSelectionChanged = new EventEmitter<any>();
	@Output() onMap = new EventEmitter<any>();
	@Output() productCreateModalClosed = new EventEmitter<any>();
	@Output() onProductMasterDataGridLoaded = new EventEmitter<any>();

	@ViewChild('productMasterDataGrid') productMasterDataGrid: DxDataGridComponent; // the third grid

	public productMasterDataSource: any = {};
	public productMappingSelectedRowMap: any; // 3rd grid selected row
	public isLoadProductMasterGridDataSource: boolean;
	public searchText;
	public companyNameHeaderDataSource;
	public masterBrandHeaderDataSource;
	public brandNameHeaderDataSource;
	public subBrandNameHeaderDataSource;
	public productNameHeaderDataSource;
	public categoryNameHeaderDataSource;
	public subcategoryFunctionNameHeaderDataSource;
	public subcategoryFormNameHeaderDataSource;
	public productSizeOnLabelHeaderDataSource;
	public currentUser: User;

	private _destroyed$ = new Subject();
	private isProductMasterDataGridSettingLoading = true;
	private isProductMasterDataLoading = false;
	private productMasterDataGridSettings: GridSettings;
	private productMasterDataGridSetting: GridSetting = new GridSetting();
	private currentProductMasterDataGridState: GridState;
	private productMasterLoadingOptions: any;

	public contextMenuItems = [
		{ text: 'Zoom In', icon: 'plus' },
		{ text: 'Share', icon: 'message' },
		{ text: 'Download', icon: 'download' }
	];
	private isEditableMode: boolean;

	constructor(
		private userService: UserApi,
		private filterBuilder: FilterBuilderService,
		private productMappingService: ProductMappingService,
		private modalService: NgbModal,
	) {
	}

	ngOnInit() {
		this.currentUser = this.userService.logonUser;
		this.productMasterDataGridSettings = {
			PageName: this.pageName,
			GridName: this.masterGridName,
			OwnedByUserId: this.currentUser.UserId
		};

		this.userService.getGridSettings(this.productMasterDataGridSettings)
			.pipe(take(1))
			.subscribe((settings) => {
				if (settings && settings.Settings) {
					if (typeof settings.Settings === 'string') {
						this.productMasterDataGridSetting = JSON.parse(settings.Settings);
					} else {
						this.productMasterDataGridSetting = settings.Settings;
					}
				} else {
					this.productMasterDataGridSettings.Settings = JSON.stringify({autosave: true});
					this.updateGridSettings(this.productMasterDataGridSettings);
				}
				this.loadProductMasterDataGridState();
			});
		this.loadColumnHeaderFilterData();
	}

	ngOnChanges(changes: SimpleChanges) {
		if (changes['isCreateProduct'].currentValue === true) {
			this.createProduct();
		}
	}

	private loadProductMasterDataGridState() {
		const productMasterDataGridState: GridState = {
			PageName: this.pageName,
			GridName: this.masterGridName,
			OwnedByUserId: this.currentUser.UserId
		};
		this.userService.getGridState(productMasterDataGridState)
			.pipe(takeUntil(this._destroyed$))
			.subscribe((state) => {
				if (state) {
					this.currentProductMasterDataGridState = state;
				} else {
					this.currentProductMasterDataGridState = {
						PageName: this.pageName,
						GridName: this.masterGridName,
						OwnedByUserId: this.currentUser.UserId,
						State: null
					};
				}
				this.loadProductMasterGridDataSource();
			});
	}

	productSelectionChangedHandler() {
		if (this.productMasterDataGrid.instance.getSelectedRowsData()[0]) {
			const selectedMasterGridRow = this.productMasterDataGrid.instance.getSelectedRowsData()[0];
			this.productMappingSelectionChanged.emit(selectedMasterGridRow);
		}
	}

	/**
	 * Context menu for Product master data grid, aka third grid
	 * @param event
	 */
	productMasterDataGridContextMenu(e) {
		if (e.target == 'content') {
			// e.items can be undefined
			if (!e.items) { e.items = []; }

			// Add a custom menu item
			e.items.push(
				{
					text: 'Refresh',
					icon: 'refresh',
					onItemClick: () => {
						this.refreshProductMasterDataGrid();
					}
				},
				{
					text: 'Grid Settings',
					items: [
						{
							text: this.productMasterDataGridSetting.autosave ? 'Disable autosave' : 'Enable autosave',
							onItemClick: () => {
								this.productMasterDataGridSetting.autosave = !this.productMasterDataGridSetting.autosave;
								this.productMasterDataGridSettings.Settings = JSON.stringify(this.productMasterDataGridSetting);
								this.updateGridSettings(this.productMasterDataGridSettings);
							}
						}
					]
				});
		}
	}

	/**
	 * Refresh Product master data grid, aka third grid
	 */
	refreshProductMasterDataGrid() {
		this.productMasterDataGrid.instance.refresh();
	}

	private updateGridSettings(gridSettings: GridSettings) {
		gridSettings.SavedOn = new Date();
		this.userService.setGridSettings(gridSettings).pipe(take(1)).subscribe();
	}

	private updateGridState(gridState: GridState) {
		gridState.SavedOn = new Date();
		this.userService.setGridState(gridState).pipe(take(1)).subscribe();
	}

	onCellPrepared(e) {
		if (e.rowType === 'data' && e.column.command === 'edit') {
			// e.cellElement.find(".dx-link-edit").remove();
			// e.cellElement.find(".dx-link-delete").remove();
		}
	}

	private loadProductMasterGridDataSource() {
		this.isProductMasterDataGridSettingLoading = false;
		this.productMasterDataSource = new CustomStore({
			key: 'ProductMasterId',
			load: (loadOptions: any) => {
				this.isProductMasterDataLoading = true;
				const optionFilters = loadOptions.filter;
				let isSearch = false;
				let filter;
				const nestedList: SearchNestedList = {};

				if (optionFilters && optionFilters.length > 2 && Array.isArray(optionFilters[2]) && optionFilters[2].length > 10) {
					isSearch = true;
					let data = new Array();
					optionFilters[0].forEach(element => {
						data.push(element);
					});

					nestedList.filterList = this.formatFilterOperators(data);
					data = [];

					nestedList.condition = optionFilters[1];
					optionFilters[2].forEach(element => {
						data.push(element);
					});
					nestedList.searchList = this.formatFilterOperators(data);
					filter = nestedList;
				} else if (optionFilters && optionFilters.length > 2 && Array.isArray(optionFilters[0]) && optionFilters[0].length > 10) {
					isSearch = true;
					let data = new Array();
					optionFilters[2].forEach(element => {
						data.push(element);
					});

					nestedList.filterList = this.formatFilterOperators(data);
					data = [];

					nestedList.condition = optionFilters[1];
					optionFilters[0].forEach(element => {
						data.push(element);
					});
					nestedList.searchList = this.formatFilterOperators(data);
					filter = nestedList;
				} else {
					filter = this.formatFilterOperators(loadOptions.filter);
				}

				// console.log('final filter', filter);
				const finalOptions: LoadOptions = loadOptions;
				const re1 = /\[/gi;
				const re2 = /\]/gi;
				finalOptions.testFilter = JSON.stringify(loadOptions.filter) ? JSON.stringify(loadOptions.filter).replace(re1, '$').replace(re2, '&') : ''; // 'loadOptions.filter';
				finalOptions.filter = filter;
				this.productMasterLoadingOptions = filter;

				if (isSearch) {
					return this.productMappingService.getProductMasterWithSearchSizeListGrid(finalOptions)
						.toPromise()
						.then((data: any) => {
							this.loadingHandler(false);
							// this.isProductMasterDataLoading = false;
							// this.loaderHandler();

							return {
								data: data.data,
								totalCount: data.totalCount,
								summary: data.summary,
								groupCount: data.groupCount
							};
						})
						.catch(error => {
							// this.isProductMasterDataLoading = false;
							this.loadingHandler(false);
							// this.loaderHandler();
							throw new Error('Data Loading Error');
						});
				} else {
					return this.productMappingService.getProductMasterWithSizeListGrid(finalOptions)
						.toPromise()
						.then((data: any) => {
							this.loadingHandler(false);
							// this.isProductMasterDataLoading = false;
							// this.loaderHandler();
							return {
								data: data.data,
								totalCount: data.totalCount,
								summary: data.summary,
								groupCount: data.groupCount
							};
						})
						.catch(error => {
							this.loadingHandler(false);
							// this.isProductMasterDataLoading = false;
							// this.loaderHandler();
							throw new Error('Data Loading Error');
						});
				}
			},
			insert: (value: any) => {
				console.log('insert', value);
				return value;
			},
			update: (key, value) => {
				console.log('update', key, value);
				return value;
			}
		});
		this.isLoadProductMasterGridDataSource = true;
	}

	loadingHandler(state: boolean) {
		this.onProductMasterDataGridLoaded.emit(state);
	}


	public loadMasterState = () => {
		return this.currentProductMasterDataGridState && this.currentProductMasterDataGridState.State ?
			JSON.parse(this.currentProductMasterDataGridState.State) : null;
	}

	public saveMasterState = (state) => {
		if (this.productMasterDataGridSetting) {
			let gridSetting: GridSetting;
			if (typeof this.productMasterDataGridSetting === 'string') {
				gridSetting = JSON.parse(this.productMasterDataGridSetting);
			} else {
				gridSetting = this.productMasterDataGridSetting;
			}
			if (gridSetting.autosave) {
				this.currentProductMasterDataGridState.State = JSON.stringify(state);
				this.updateGridState(this.currentProductMasterDataGridState);
			}
		}
	}

	formatFilterOperators(rawFilter: any[]) {
		if (!rawFilter) {
			return null;
		}
		const filterData = this.filterBuilder.handleNotFilterFormatting(rawFilter);
		return this.filterBuilder.filterFormatting(filterData);
	}

	private loadColumnHeaderFilterData() {
		this.companyNameHeaderDataSource = new CustomStore({
			load: (loadOptions: any) => {
				const columnName = 'CompanyName';
				loadOptions.column = 'CompanyName';
				loadOptions.valueColumn = 'CompanyId';
				loadOptions.filter = this.clearHeaderFilterParams(this.productMasterLoadingOptions, columnName);
				return this.getHeaderFiltersProductMaster(loadOptions);
			}
		});

		this.masterBrandHeaderDataSource = new CustomStore({
			load: (loadOptions: any) => {
				const columnName = 'MasterBrandName';
				loadOptions.column = 'MasterBrandName';
				loadOptions.valueColumn = 'MasterBrandId';
				loadOptions.filter = this.clearHeaderFilterParams(this.productMasterLoadingOptions, columnName);
				return this.getHeaderFiltersProductMaster(loadOptions);
			}
		});

		this.brandNameHeaderDataSource = new CustomStore({
			load: (loadOptions: any) => {
				const columnName = 'BrandName';
				loadOptions.column = columnName;
				loadOptions.valueColumn = '';
				loadOptions.filter = this.clearHeaderFilterParams(this.productMasterLoadingOptions, columnName);
				return this.getHeaderFiltersProductMaster(loadOptions);
			}
		});

		this.subBrandNameHeaderDataSource =  new CustomStore({
			load: (loadOptions: any) => {
				const columnName = 'SubBrandName';
				loadOptions.column = columnName;
				loadOptions.valueColumn = '';
				loadOptions.filter = this.clearHeaderFilterParams(this.productMasterLoadingOptions, columnName);
				return this.getHeaderFiltersProductMaster(loadOptions);
			}
		});

		this.productNameHeaderDataSource = new CustomStore({
			load: (loadOptions: any) => {
				const columnName = 'ProductName';
				loadOptions.column = columnName;
				loadOptions.valueColumn = '';
				loadOptions.filter = this.clearHeaderFilterParams(this.productMasterLoadingOptions, columnName);
				return this.getHeaderFiltersProductMaster(loadOptions);
			}
		});

		this.categoryNameHeaderDataSource = new CustomStore({
			load: (loadOptions: any) => {
				loadOptions.column = 'CategoryName';
				loadOptions.valueColumn = '';
				return this.getHeaderFiltersProductMaster(loadOptions);
			}
		});

		this.subcategoryFunctionNameHeaderDataSource = new CustomStore({
			load: (loadOptions: any) => {
				loadOptions.column = 'SubcategoryFunctionName';
				loadOptions.valueColumn = '';

				return this.getHeaderFiltersProductMaster(loadOptions);
			}
		});

		this.subcategoryFormNameHeaderDataSource = new CustomStore({
			load: (loadOptions: any) => {
				loadOptions.column = 'SubcategoryFormName';
				loadOptions.valueColumn = '';

				return this.getHeaderFiltersProductMaster(loadOptions);
			}
		});

		this.productSizeOnLabelHeaderDataSource = new CustomStore({
			load: (loadOptions: any) => {
				loadOptions.column = 'ProductSizeOnLabel';
				loadOptions.valueColumn = '';

				return this.getHeaderFiltersProductMaster(loadOptions);
			}
		});
	}

	private clearHeaderFilterParams(loadingFilter: FilterOperationModel[], columnName: string) {
		let res = null;
		if (loadingFilter && Array.isArray(loadingFilter)) {
			res = loadingFilter.filter(item => item.column !== columnName);
			return res;
		} else if (loadingFilter) {
			const filterList = loadingFilter['filterList'];
			if (filterList) {
				const res = filterList.filter(item => item.column !== columnName);
				return res;
			}
		}
		return res;
	}

	getHeaderFiltersProductMaster(loadingOption) {
		return this.productMappingService.getHeaderFiltersProductMaster(loadingOption)
			.toPromise()
			.catch(error => { throw new Error('Data Loading Error'); });
	}

	customizeProductMasterId(cellInfo) {
		return 'PMs_' + cellInfo.value;
	}

	editOrCloneProduct(data, isCloning) {
		const modalRef = this.modalService.open(ProductEditComponent, {windowClass: 'product-edit-container', size: 'xl', backdrop: true});
		modalRef.componentInstance.data = data;
		modalRef.componentInstance.isCloning = isCloning;
		modalRef.componentInstance.currentUser = this.currentUser;
		modalRef.result.then(result => {
			// onclose
			if (result === 'save') {
				this.refreshProductMasterDataGrid();
			}
			if (result === 'cancel') {
				this.refreshProductMasterDataGrid();
			}
		}, (reason) => {
			// ondismiss
			this.refreshProductMasterDataGrid();
		});
	}

	map(productMasterId) {
		this.onMap.emit(productMasterId);
	}

	createProduct() {
		const modalRef = this.modalService.open(ProductEditComponent,
			{windowClass: 'product-edit-container', size: 'xl', backdrop: true
			});
		modalRef.componentInstance.data = undefined;
		modalRef.componentInstance.currentUser = this.currentUser;
		modalRef.result.then(result => {
			// onclose
			this.productCreateModalClosed.emit();
			if (result === 'save') {
				this.refreshProductMasterDataGrid();
			}
			if (result === 'cancel') {
				this.refreshProductMasterDataGrid();
			}
		}, (reason) => {
			// ondismiss
			this.refreshProductMasterDataGrid();
		});
	}

	switchToEditableMode() {
		this.isEditableMode = !this.isEditableMode;
		this.productMasterDataGrid.instance.option('editing.mode', 'row');
	}
}
